import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminJobApplicationsViewer from './AdminJobApplicationsViewer';

const JobPostingsManager = () => {
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null); // For application viewer

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [salaryRange, setSalaryRange] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');
  const [qualifications, setQualifications] = useState('');
  const [responsibilities, setResponsibilities] = useState('');
  const [benefits, setBenefits] = useState('');
  const [applicationDeadline, setApplicationDeadline] = useState('');
  const [editingJobId, setEditingJobId] = useState(null);

  const fetchJobs = async () => {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/job/all`, {
      withCredentials: true,
    });
    setJobs(res.data.jobs || []);
  };

  const handleCreateOrEdit = async () => {
    if (!title || !description) return alert('Title and description required');

    const payload = {
      title,
      description,
      location,
      salaryRange,
      employmentType,
      experienceLevel,
      qualifications,
      responsibilities: responsibilities.split(',').map((r) => r.trim()),
      benefits: benefits.split(',').map((b) => b.trim()),
      applicationDeadline,
    };

    if (editingJobId) {
      await axios.put(`${process.env.REACT_APP_API}/admin/job/edit/${editingJobId}`, payload, {
        withCredentials: true,
      });
    } else {
      await axios.post(`${process.env.REACT_APP_API}/admin/job/create`, payload, {
        withCredentials: true,
      });
    }

    resetForm();
    fetchJobs();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${process.env.REACT_APP_API}/admin/job/delete/${id}`, {
      withCredentials: true,
    });
    fetchJobs();
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setLocation('');
    setSalaryRange('');
    setEmploymentType('');
    setExperienceLevel('');
    setQualifications('');
    setResponsibilities('');
    setBenefits('');
    setApplicationDeadline('');
    setEditingJobId(null);
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  return (
    <div className="job-manager">
      <style>{`
        .job-manager {
          font-family: Arial, sans-serif;
          padding: 2rem;
          background: #f9f9f9;
        }
        .job-form, .job-card {
          background: #fff;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .job-form input,
        .job-form textarea {
          width: 100%;
          margin: 0.5rem 0;
          padding: 0.75rem;
          font-size: 1rem;
          border: 1px solid #ccc;
          border-radius: 6px;
        }
        .job-form button {
          margin-top: 1rem;
          padding: 0.75rem 1.5rem;
          font-size: 1rem;
          border: none;
          border-radius: 6px;
          background-color: #007bff;
          color: white;
          cursor: pointer;
        }
        .job-form button:hover {
          background-color: #0056b3;
        }
        .job-card {
          border-left: 4px solid #007bff;
        }
        .job-card h4 {
          margin-top: 0;
          color: #333;
        }
        .job-card p {
          margin: 0.25rem 0;
        }
        .job-card small {
          display: block;
          color: #666;
          margin-bottom: 0.5rem;
        }
        .job-card .btn-group {
          margin-top: 1rem;
        }
        .job-card button {
          margin-right: 1rem;
          padding: 0.5rem 1rem;
          font-size: 0.9rem;
          border: none;
          border-radius: 5px;
          cursor: pointer;
        }
        .job-card .edit {
          background-color: #ffc107;
          color: white;
        }
        .job-card .delete {
          background-color: #dc3545;
          color: white;
        }
        .job-card .applications {
          background-color: #28a745;
          color: white;
        }
      `}</style>

      <div className="job-form">
        <h3>{editingJobId ? 'Edit Job Posting' : 'Create Job Posting'}</h3>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Job Title" />
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Job Description" />
        <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location" />
        <input value={salaryRange} onChange={(e) => setSalaryRange(e.target.value)} placeholder="Salary Range" />
        <input value={employmentType} onChange={(e) => setEmploymentType(e.target.value)} placeholder="Employment Type (e.g., Full-Time)" />
        <input value={experienceLevel} onChange={(e) => setExperienceLevel(e.target.value)} placeholder="Experience Level (e.g., Mid-Level)" />
        <textarea value={qualifications} onChange={(e) => setQualifications(e.target.value)} placeholder="Qualifications" />
        <textarea value={responsibilities} onChange={(e) => setResponsibilities(e.target.value)} placeholder="Responsibilities (comma separated)" />
        <textarea value={benefits} onChange={(e) => setBenefits(e.target.value)} placeholder="Benefits (comma separated)" />
        <input type="date" value={applicationDeadline} onChange={(e) => setApplicationDeadline(e.target.value)} />
        <button onClick={handleCreateOrEdit}>{editingJobId ? 'Update' : 'Create'}</button>
      </div>

      <h3>All Job Postings</h3>
      {jobs.map((job) => (
        <div key={job._id} className="job-card">
          <h4>{job.title}</h4>
          <p>{job.description}</p>
          <small>{job.location} | {job.salaryRange} | {job.employmentType} | {job.experienceLevel}</small>
          <p><strong>Qualifications:</strong> {job.qualifications}</p>
          <p><strong>Responsibilities:</strong> {job.responsibilities?.join(', ')}</p>
          <p><strong>Benefits:</strong> {job.benefits?.join(', ')}</p>
          <p><strong>Deadline:</strong> {job.applicationDeadline?.split('T')[0]}</p>

          <div className="btn-group">
            <button className="edit" onClick={() => {
              setEditingJobId(job._id);
              setTitle(job.title);
              setDescription(job.description);
              setLocation(job.location || '');
              setSalaryRange(job.salaryRange || '');
              setEmploymentType(job.employmentType || '');
              setExperienceLevel(job.experienceLevel || '');
              setQualifications(job.qualifications || '');
              setResponsibilities((job.responsibilities || []).join(', '));
              setBenefits((job.benefits || []).join(', '));
              setApplicationDeadline(job.applicationDeadline?.split('T')[0] || '');
            }}>Edit</button>
            <button className="delete" onClick={() => handleDelete(job._id)}>Delete</button>
            <button
              className="applications"
              onClick={() =>
                selectedJobId === job._id ? setSelectedJobId(null) : setSelectedJobId(job._id)
              }
            >
              {selectedJobId === job._id ? 'Hide Applications' : 'View Applications'}
            </button>
          </div>

          {selectedJobId === job._id && (
            <AdminJobApplicationsViewer
              jobId={job._id}
              onClose={() => setSelectedJobId(null)}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default JobPostingsManager;






// import React, { useEffect, useState } from 'react';
// import axios from 'axios';

// const JobPostingsManager = () => {
//   const [jobs, setJobs] = useState([]);

//   // Form state
//   const [title, setTitle] = useState('');
//   const [description, setDescription] = useState('');
//   const [location, setLocation] = useState('');
//   const [salaryRange, setSalaryRange] = useState('');
//   const [employmentType, setEmploymentType] = useState('');
//   const [experienceLevel, setExperienceLevel] = useState('');
//   const [qualifications, setQualifications] = useState('');
//   const [responsibilities, setResponsibilities] = useState('');
//   const [benefits, setBenefits] = useState('');
//   const [applicationDeadline, setApplicationDeadline] = useState('');
//   const [editingJobId, setEditingJobId] = useState(null);

//   const fetchJobs = async () => {
//     const res = await axios.get(`${process.env.REACT_APP_API}/admin/job/all`, {
//       withCredentials: true,
//     });
//     setJobs(res.data.jobs || []);
//   };

//   const handleCreateOrEdit = async () => {
//     if (!title || !description) return alert('Title and description required');

//     const payload = {
//       title,
//       description,
//       location,
//       salaryRange,
//       employmentType,
//       experienceLevel,
//       qualifications,
//       responsibilities: responsibilities.split(',').map((r) => r.trim()),
//       benefits: benefits.split(',').map((b) => b.trim()),
//       applicationDeadline,
//     };

//     if (editingJobId) {
//       await axios.put(`${process.env.REACT_APP_API}/admin/job/edit/${editingJobId}`, payload, {
//         withCredentials: true,
//       });
//     } else {
//       await axios.post(`${process.env.REACT_APP_API}/admin/job/create`, payload, {
//         withCredentials: true,
//       });
//     }

//     resetForm();
//     fetchJobs();
//   };

//   const handleDelete = async (id) => {
//     await axios.delete(`${process.env.REACT_APP_API}/admin/job/delete/${id}`, {
//       withCredentials: true,
//     });
//     fetchJobs();
//   };

//   const resetForm = () => {
//     setTitle('');
//     setDescription('');
//     setLocation('');
//     setSalaryRange('');
//     setEmploymentType('');
//     setExperienceLevel('');
//     setQualifications('');
//     setResponsibilities('');
//     setBenefits('');
//     setApplicationDeadline('');
//     setEditingJobId(null);
//   };

//   useEffect(() => {
//     fetchJobs();
//   }, []);

//   return (
//     <div className="job-manager">
//       <style>{`
//         .job-manager {
//           font-family: Arial, sans-serif;
//           padding: 2rem;
//           background: #f9f9f9;
//         }
//         .job-form, .job-card {
//           background: #fff;
//           border-radius: 8px;
//           padding: 1.5rem;
//           margin-bottom: 2rem;
//           box-shadow: 0 2px 8px rgba(0,0,0,0.1);
//         }
//         .job-form input,
//         .job-form textarea {
//           width: 100%;
//           margin: 0.5rem 0;
//           padding: 0.75rem;
//           font-size: 1rem;
//           border: 1px solid #ccc;
//           border-radius: 6px;
//         }
//         .job-form button {
//           margin-top: 1rem;
//           padding: 0.75rem 1.5rem;
//           font-size: 1rem;
//           border: none;
//           border-radius: 6px;
//           background-color: #007bff;
//           color: white;
//           cursor: pointer;
//         }
//         .job-form button:hover {
//           background-color: #0056b3;
//         }
//         .job-card {
//           border-left: 4px solid #007bff;
//         }
//         .job-card h4 {
//           margin-top: 0;
//           color: #333;
//         }
//         .job-card p {
//           margin: 0.25rem 0;
//         }
//         .job-card small {
//           display: block;
//           color: #666;
//           margin-bottom: 0.5rem;
//         }
//         .job-card .btn-group {
//           margin-top: 1rem;
//         }
//         .job-card button {
//           margin-right: 1rem;
//           padding: 0.5rem 1rem;
//           font-size: 0.9rem;
//           border: none;
//           border-radius: 5px;
//           cursor: pointer;
//         }
//         .job-card .edit {
//           background-color: #ffc107;
//           color: white;
//         }
//         .job-card .delete {
//           background-color: #dc3545;
//           color: white;
//         }
//       `}</style>

//       <div className="job-form">
//         <h3>{editingJobId ? 'Edit Job Posting' : 'Create Job Posting'}</h3>
//         <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Job Title" />
//         <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Job Description" />
//         <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location" />
//         <input value={salaryRange} onChange={(e) => setSalaryRange(e.target.value)} placeholder="Salary Range" />
//         <input value={employmentType} onChange={(e) => setEmploymentType(e.target.value)} placeholder="Employment Type (e.g., Full-Time)" />
//         <input value={experienceLevel} onChange={(e) => setExperienceLevel(e.target.value)} placeholder="Experience Level (e.g., Mid-Level)" />
//         <textarea value={qualifications} onChange={(e) => setQualifications(e.target.value)} placeholder="Qualifications" />
//         <textarea value={responsibilities} onChange={(e) => setResponsibilities(e.target.value)} placeholder="Responsibilities (comma separated)" />
//         <textarea value={benefits} onChange={(e) => setBenefits(e.target.value)} placeholder="Benefits (comma separated)" />
//         <input type="date" value={applicationDeadline} onChange={(e) => setApplicationDeadline(e.target.value)} />
//         <button onClick={handleCreateOrEdit}>{editingJobId ? 'Update' : 'Create'}</button>
//       </div>

//       <h3>All Job Postings</h3>
//       {jobs.map((job) => (
//         <div key={job._id} className="job-card">
//           <h4>{job.title}</h4>
//           <p>{job.description}</p>
//           <small>{job.location} | {job.salaryRange} | {job.employmentType} | {job.experienceLevel}</small>
//           <p><strong>Qualifications:</strong> {job.qualifications}</p>
//           <p><strong>Responsibilities:</strong> {job.responsibilities?.join(', ')}</p>
//           <p><strong>Benefits:</strong> {job.benefits?.join(', ')}</p>
//           <p><strong>Deadline:</strong> {job.applicationDeadline?.split('T')[0]}</p>
//           <div className="btn-group">
//             <button className="edit" onClick={() => {
//               setEditingJobId(job._id);
//               setTitle(job.title);
//               setDescription(job.description);
//               setLocation(job.location || '');
//               setSalaryRange(job.salaryRange || '');
//               setEmploymentType(job.employmentType || '');
//               setExperienceLevel(job.experienceLevel || '');
//               setQualifications(job.qualifications || '');
//               setResponsibilities((job.responsibilities || []).join(', '));
//               setBenefits((job.benefits || []).join(', '));
//               setApplicationDeadline(job.applicationDeadline?.split('T')[0] || '');
//             }}>Edit</button>
//             <button className="delete" onClick={() => handleDelete(job._id)}>Delete</button>
//           </div>
//         </div>
//       ))}
//     </div>
//   );
// };

// export default JobPostingsManager;



// import React, { useEffect, useState } from 'react';
// import axios from 'axios';

// const JobPostingsManager = () => {
//   const [jobs, setJobs] = useState([]);

//   // Form fields
//   const [title, setTitle] = useState('');
//   const [description, setDescription] = useState('');
//   const [location, setLocation] = useState('');
//   const [salaryRange, setSalaryRange] = useState('');
//   const [employmentType, setEmploymentType] = useState('');
//   const [experienceLevel, setExperienceLevel] = useState('');
//   const [qualifications, setQualifications] = useState('');
//   const [responsibilities, setResponsibilities] = useState('');
//   const [benefits, setBenefits] = useState('');
//   const [applicationDeadline, setApplicationDeadline] = useState('');
//   const [editingJobId, setEditingJobId] = useState(null);

//   const fetchJobs = async () => {
//     const res = await axios.get(`${process.env.REACT_APP_API}/admin/job/all`, { withCredentials: true });
//     setJobs(res.data.jobs || []);
//   };

//   const handleCreateOrEdit = async () => {
//     if (!title || !description) return alert("Title and description required");

//     const payload = {
//       title,
//       description,
//       location,
//       salaryRange,
//       employmentType,
//       experienceLevel,
//       qualifications,
//       responsibilities: responsibilities.split(',').map(r => r.trim()),
//       benefits: benefits.split(',').map(b => b.trim()),
//       applicationDeadline
//     };

//     if (editingJobId) {
//       await axios.put(`${process.env.REACT_APP_API}/admin/job/edit/${editingJobId}`, payload, { withCredentials: true });
//     } else {
//       await axios.post(`${process.env.REACT_APP_API}/admin/job/create`, payload, { withCredentials: true });
//     }

//     resetForm();
//     fetchJobs();
//   };

//   const handleDelete = async (id) => {
//     await axios.delete(`${process.env.REACT_APP_API}/admin/job/delete/${id}`, { withCredentials: true });
//     fetchJobs();
//   };

//   const resetForm = () => {
//     setTitle('');
//     setDescription('');
//     setLocation('');
//     setSalaryRange('');
//     setEmploymentType('');
//     setExperienceLevel('');
//     setQualifications('');
//     setResponsibilities('');
//     setBenefits('');
//     setApplicationDeadline('');
//     setEditingJobId(null);
//   };

//   useEffect(() => {
//     fetchJobs();
//   }, []);

//   return (
//     <div>
//       <h3>{editingJobId ? "Edit Job Posting" : "Create Job Posting"}</h3>
//       <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Job Title" />
//       <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Job Description" />
//       <input value={location} onChange={e => setLocation(e.target.value)} placeholder="Location" />
//       <input value={salaryRange} onChange={e => setSalaryRange(e.target.value)} placeholder="Salary Range" />
//       <input value={employmentType} onChange={e => setEmploymentType(e.target.value)} placeholder="Employment Type (e.g., Full-Time)" />
//       <input value={experienceLevel} onChange={e => setExperienceLevel(e.target.value)} placeholder="Experience Level (e.g., Mid-Level)" />
//       <textarea value={qualifications} onChange={e => setQualifications(e.target.value)} placeholder="Qualifications" />
//       <textarea value={responsibilities} onChange={e => setResponsibilities(e.target.value)} placeholder="Responsibilities (comma separated)" />
//       <textarea value={benefits} onChange={e => setBenefits(e.target.value)} placeholder="Benefits (comma separated)" />
//       <input type="date" value={applicationDeadline} onChange={e => setApplicationDeadline(e.target.value)} placeholder="Application Deadline" />
//       <button onClick={handleCreateOrEdit}>{editingJobId ? "Update" : "Create"}</button>

//       <hr />
//       <h3>All Job Postings</h3>
//       {jobs.map((job) => (
//         <div key={job._id}>
//           <h4>{job.title}</h4>
//           <p>{job.description}</p>
//           <small>{job.location} | {job.salaryRange} | {job.employmentType} | {job.experienceLevel}</small>
//           <p><strong>Qualifications:</strong> {job.qualifications}</p>
//           <p><strong>Responsibilities:</strong> {job.responsibilities?.join(', ')}</p>
//           <p><strong>Benefits:</strong> {job.benefits?.join(', ')}</p>
//           <p><strong>Deadline:</strong> {job.applicationDeadline?.split('T')[0]}</p>
//           <button onClick={() => {
//             setEditingJobId(job._id);
//             setTitle(job.title);
//             setDescription(job.description);
//             setLocation(job.location || '');
//             setSalaryRange(job.salaryRange || '');
//             setEmploymentType(job.employmentType || '');
//             setExperienceLevel(job.experienceLevel || '');
//             setQualifications(job.qualifications || '');
//             setResponsibilities((job.responsibilities || []).join(', '));
//             setBenefits((job.benefits || []).join(', '));
//             setApplicationDeadline(job.applicationDeadline?.split('T')[0] || '');
//           }}>Edit</button>
//           <button onClick={() => handleDelete(job._id)}>Delete</button>
//         </div>
//       ))}
//     </div>
//   );
// };

// export default JobPostingsManager;








// import React, { useEffect, useState } from 'react';
// import axios from 'axios';
// import { useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';

// const JobPostingsManager = () => {
//   const [jobs, setJobs] = useState([]);
//   const [title, setTitle] = useState('');
//   const [description, setDescription] = useState('');
//   const [editingJobId, setEditingJobId] = useState(null);
//   const [authChecked, setAuthChecked] = useState(false);
//   const [isAuthorized, setIsAuthorized] = useState(false);
//   const navigate = useNavigate();

//   // ✅ Admin authentication check
//   useEffect(() => {
//     const checkAdminSession = async () => {
//       try {
//         const res = await axios.get(`${process.env.REACT_APP_API}/check/session`, {
//           withCredentials: true
//         });

//         console.log('[JobPostingsManager] Auth check response:', res.data);

//         if (res.data.isAuthenticated && res.data.user?.accountType === 'Admin') {
//           setIsAuthorized(true);
//         } else {
//           toast.error('Unauthorized: Admin access only.');
//           navigate('/admin/form/11');
//         }
//       } catch (err) {
//         console.error('[JobPostingsManager] Auth error:', err);
//         toast.error('Failed to authenticate as admin.');
//         navigate('/admin/form/11');
//       } finally {
//         setAuthChecked(true);
//       }
//     };

//     checkAdminSession();
//   }, [navigate]);

//   // ✅ Fetch jobs after auth is verified
//   useEffect(() => {
//     if (authChecked && isAuthorized) {
//       fetchJobs();
//     }
//   }, [authChecked, isAuthorized]);

//   const fetchJobs = async () => {
//     try {
//       const res = await axios.get(`${process.env.REACT_APP_API}/admin/job/all`, { withCredentials: true });
//       setJobs(res.data.jobs || []);
//     } catch (err) {
//       console.error('[JobPostingsManager] Fetch jobs error:', err);
//       toast.error('Failed to load job postings.');
//     }
//   };

//   const handleCreateOrEdit = async () => {
//     if (!title || !description) return toast.error("Title and description required");

//     const payload = { title, description };

//     try {
//       if (editingJobId) {
//         await axios.put(`${process.env.REACT_APP_API}/admin/job/edit/${editingJobId}`, payload, {
//           withCredentials: true
//         });
//         toast.success('Job updated!');
//       } else {
//         await axios.post(`${process.env.REACT_APP_API}/admin/job/create`, payload, {
//           withCredentials: true
//         });
//         toast.success('Job created!');
//       }

//       setTitle('');
//       setDescription('');
//       setEditingJobId(null);
//       fetchJobs();
//     } catch (err) {
//       console.error('[JobPostingsManager] Create/Edit error:', err);
//       toast.error('Failed to create or update job.');
//     }
//   };

//   const handleDelete = async (id) => {
//     if (!window.confirm('Are you sure you want to delete this job?')) return;

//     try {
//       await axios.delete(`${process.env.REACT_APP_API}/admin/job/delete/${id}`, {
//         withCredentials: true
//       });
//       toast.success('Job deleted!');
//       fetchJobs();
//     } catch (err) {
//       console.error('[JobPostingsManager] Delete error:', err);
//       toast.error('Failed to delete job.');
//     }
//   };

//   if (!authChecked) return <p>Checking admin access...</p>;
//   if (!isAuthorized) return null;

//   return (
//     <div>
//       <h3>{editingJobId ? "Edit Job Posting" : "Create Job Posting"}</h3>
//       <input
//         value={title}
//         onChange={(e) => setTitle(e.target.value)}
//         placeholder="Job Title"
//       />
//       <textarea
//         value={description}
//         onChange={(e) => setDescription(e.target.value)}
//         placeholder="Job Description"
//       />
//       <button onClick={handleCreateOrEdit}>
//         {editingJobId ? "Update" : "Create"}
//       </button>

//       <hr />
//       <h3>All Job Postings</h3>
//       {jobs.map((job) => (
//         <div key={job._id} style={{ marginBottom: '1rem' }}>
//           <h4>{job.title}</h4>
//           <p>{job.description}</p>
//           <button
//             onClick={() => {
//               setEditingJobId(job._id);
//               setTitle(job.title);
//               setDescription(job.description);
//             }}
//           >
//             Edit
//           </button>
//           <button onClick={() => handleDelete(job._id)}>Delete</button>
//         </div>
//       ))}
//     </div>
//   );
// };

// export default JobPostingsManager;
